import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader2, Save, ArrowLeft } from 'lucide-react'
import useCountryStore from '@/stores/countryStore'
import { CreateCountryDto, UpdateCountryDto, Currency } from '@/services/configService'
import configService from '@/services/configService'

const CountryForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const {
    currentCountry,
    loading,
    error,
    fetchCountryById,
    createCountry,
    updateCountry,
    clearError,
    clearCurrentCountry
  } = useCountryStore()

  const [formData, setFormData] = useState<CreateCountryDto>({
    code: '',
    name: '',
    native_name: '',
    flag_emoji: '',
    default_currency_id: '',
    default_language: 'fr',
    timezone: 'Europe/Paris',
    enabled: true,
    tax_calculation: 'inclusive',
    phone_code: '',
    continent: 'Europe'
  })

  const [submitting, setSubmitting] = useState(false)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [loadingCurrencies, setLoadingCurrencies] = useState(false)

  const continents = [
    'Europe',
    'North America',
    'South America',
    'Asia',
    'Africa',
    'Oceania',
    'Antarctica'
  ]

  const languages = [
    { code: 'fr', name: 'Français' },
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
    { code: 'pt', name: 'Português' },
    { code: 'ru', name: 'Русский' },
    { code: 'zh', name: '中文' },
    { code: 'ja', name: '日本語' },
    { code: 'ar', name: 'العربية' }
  ]

  const timezones = [
    'Europe/Paris',
    'Europe/London',
    'America/New_York',
    'America/Los_Angeles',
    'America/Toronto',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'Africa/Cairo',
    'UTC'
  ]

  useEffect(() => {
    // Charger les devises
    const loadCurrencies = async () => {
      setLoadingCurrencies(true)
      try {
        const response = await configService.currencies.getAll({ enabled: true })
        setCurrencies(response.data)
        // Si c'est un nouveau pays, sélectionner la première devise par défaut
        if (!isEditMode && response.data.length > 0 && !formData.default_currency_id) {
          setFormData(prev => ({
            ...prev,
            default_currency_id: response.data[0].id
          }))
        }
      } catch (error) {
        console.error('Error loading currencies:', error)
      } finally {
        setLoadingCurrencies(false)
      }
    }

    loadCurrencies()

    if (isEditMode && id) {
      fetchCountryById(id)
    }

    return () => {
      clearCurrentCountry()
      clearError()
    }
  }, [id, isEditMode])

  useEffect(() => {
    if (isEditMode && currentCountry) {
      setFormData({
        code: currentCountry.code,
        name: currentCountry.name,
        native_name: currentCountry.native_name || '',
        flag_emoji: currentCountry.flag_emoji || '',
        default_currency_id: currentCountry.default_currency_id,
        default_language: currentCountry.default_language,
        timezone: currentCountry.timezone,
        enabled: currentCountry.enabled,
        tax_calculation: currentCountry.tax_calculation,
        phone_code: currentCountry.phone_code || '',
        continent: currentCountry.continent || 'Europe'
      })
    }
  }, [currentCountry, isEditMode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    clearError()

    try {
      if (isEditMode && id) {
        const updateData: UpdateCountryDto = { ...formData }
        await updateCountry(id, updateData)
      } else {
        await createCountry(formData)
      }
      navigate('/countries')
    } catch (error) {
      console.error('Error saving country:', error)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode ? 'Modifier le pays' : 'Ajouter un pays'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode 
              ? 'Modifiez les informations du pays' 
              : 'Ajoutez un nouveau pays au système'}
          </p>
        </div>
        <button
          onClick={() => navigate('/countries')}
          className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Informations de base</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Code du pays <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="FR"
                />
                <p className="text-xs text-gray-500 mt-1">Code ISO 3166-1 alpha-2 (2 lettres)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom du pays <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="France"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Nom natif
              </label>
              <input
                type="text"
                name="native_name"
                value={formData.native_name}
                onChange={handleChange}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="France"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Emoji du drapeau
              </label>
              <input
                type="text"
                name="flag_emoji"
                value={formData.flag_emoji}
                onChange={handleChange}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="🇫🇷"
              />
              <p className="text-xs text-gray-500 mt-1">Emoji Unicode du drapeau du pays</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Configuration régionale</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Continent <span className="text-red-500">*</span>
                </label>
                <select
                  name="continent"
                  value={formData.continent}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Sélectionner un continent</option>
                  {continents.map(continent => (
                    <option key={continent} value={continent}>
                      {continent}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Devise par défaut <span className="text-red-500">*</span>
                </label>
                {loadingCurrencies ? (
                  <div className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Chargement des devises...
                  </div>
                ) : (
                  <select
                    name="default_currency_id"
                    value={formData.default_currency_id}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Sélectionner une devise</option>
                    {currencies.map(currency => (
                      <option key={currency.id} value={currency.id}>
                        {currency.code} - {currency.name} ({currency.symbol})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Langue par défaut <span className="text-red-500">*</span>
                </label>
                <select
                  name="default_language"
                  value={formData.default_language}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {languages.map(language => (
                    <option key={language.code} value={language.code}>
                      {language.name} ({language.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Fuseau horaire <span className="text-red-500">*</span>
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Indicatif téléphonique
                </label>
                <input
                  type="text"
                  name="phone_code"
                  value={formData.phone_code}
                  onChange={handleChange}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="+33"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Paramètres fiscaux</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Calcul de la TVA
                </label>
                <select
                  name="tax_calculation"
                  value={formData.tax_calculation}
                  onChange={handleChange}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="inclusive">TTC (TVA incluse)</option>
                  <option value="exclusive">HT (TVA exclue)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Définit si les prix sont affichés TTC ou HT
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enabled"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="enabled" className="text-sm font-medium">
                  Pays activé
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/countries')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditMode ? 'Mettre à jour' : 'Créer le pays'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CountryForm
